import EditTradeContext from "@shared/features/trades/EditTrade/EditTrade.context";
import {
  FC,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { multiplyFloats, round } from "@shared/utils/numbers";
import { sumBy } from "lodash";
import {
  OrderTypes,
  TradeSource,
  ValidationOrderData,
  ValidationOrdersData,
} from "@shared/features/trades/common/types";
import {
  DisplayResultMessage,
  EditTradeTarget,
  LanguageNameSpaces,
  OpenMessage,
  OrderStatus,
  PrecisionType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { EditOrderData } from "@shared/features/trades/EditTrade/types";
import { ValueOf } from "@shared/types/utils";
import {
  getEditWeightedAverageEntryPrice,
  getTradeAnnotations,
  getTradeTotalAmount,
  validateEditTradeOrders,
  validateSaveOrder,
} from "@shared/features/trades/common/utils";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import { getMinAndMaxPrices } from "@shared/features/tradingTerminal/common/utils";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { sharedLib } from "@shared/services/shared.service";
import useTradeAmounts from "@shared/features/trades/EditTrade/useTradeAmounts";
import useEditTradeOrders from "@shared/features/trades/EditTrade/useEditTradeOrders";
import { UseGeneralConfigurationProps } from "@shared/features/configuration/types";
import React from "react";
import { useRefValue } from "@shared/utils/hooks/misc";
import { TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { useTheme } from "styled-components";

interface EditTradeWrapperProps {
  isEdit: boolean;
  tab: ValueOf<typeof TRADE_EXTRA_DETAILS_TABS>;
  tradeId?: number;
  confirmModalContextHolder: ReactNode;
  confirmWithCallback: (message: string) => Promise<boolean>;
  displayResultMessage: DisplayResultMessage;
  openMessage: OpenMessage;
  useGeneralConfiguration: (props: UseGeneralConfigurationProps) => any;
  showUnsavedChangesWarning: () => Promise<boolean>;
  showConfirmEditWarning: () => Promise<boolean>;
  exitEditMode: (response?: { data: number }) => void;
}

const EditTradeWrapper: FC<PropsWithChildren<EditTradeWrapperProps>> = ({
  children,
  isEdit,
  tab,
  tradeId,
  confirmModalContextHolder,
  confirmWithCallback,
  displayResultMessage,
  openMessage,
  useGeneralConfiguration,
  showUnsavedChangesWarning,
  showConfirmEditWarning,
  exitEditMode: exitEditModeProps,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    symbol,
    exchange,
    ordersData,
    getTradeExtraInfoOrdersStatus,
    configurationData,
    isBacktesting,
    tradeMetadata,
    activitiesAnnotations,
  } = useContext(TradeDetailsContext)!;
  const { getExchangePriceFactorLimits } = useContext(
    ExchangesMetadataContext
  )!;
  const [valuesModified, setValuesModified] = useState(false);
  const [addAmountModalOpen, setAddAmountModalOpen] = useState(false);
  const [addQuantityModalOpen, setAddQuantityModalOpen] = useState(false);
  const [editTrade, { isLoading: editTradeLoading }] =
    sharedLib.sharedApi.trades.useEditTradeMutation();
  const [editTradeConfiguration, { isLoading: editTradeConfigurationLoading }] =
    sharedLib.sharedApi.trades.useEditTradeConfigurationMutation();
  const priceFactorLimits = getExchangePriceFactorLimits(
    ordersData?.data.exchange
  );
  const valuesModifiedRef = useRefValue(valuesModified);
  const { getPrecision } = usePrecisions();
  const precision = getPrecision(PrecisionType.PRICE, symbol, exchange);
  const appTheme = useTheme();

  useEffect(() => {
    if (isEdit) {
      try {
        getTradeExtraInfoOrdersStatus.refetch();
      } catch {}
    }
  }, [isEdit]);

  const precisionConfig = ordersData?.precision;
  const {
    generalConfiguration,
    configurationModified,
    cancelChanges: cancelConfigurationChanges,
    modifiedConfiguration,
  } = useGeneralConfiguration({
    configuration: configurationData?.data,
    editable: isEdit && tab === TRADE_EXTRA_DETAILS_TABS.CONFIGURATION,
    showOverrides: false,
    precisionConfig,
    spacing: 15,
    useCard: false,
    addEditWarningMessage: !isBacktesting,
  });

  const accountId = ordersData?.data.account_id;

  useEffect(() => {
    if (configurationModified) {
      setValuesModified(true);
    }
  }, [configurationModified]);

  const entryOrderCards = ordersData?.data.entry_order_cards;
  const originalTradeAmount = useMemo(
    () => getTradeTotalAmount(entryOrderCards || []),
    [entryOrderCards, precisionConfig?.amount]
  );

  const originalTradeExistingQuantity = round(
    (
      ordersData?.data.entry_order_cards.filter(
        (order) =>
          order.status === OrderStatus.FULFILLED &&
          order.reason.toLowerCase() === "existing quantity"
      ) || []
    ).reduce((acc, order) => {
      acc += order.quantity;
      return acc;
    }, 0),
    precisionConfig?.quantity || 0
  );

  const onValuesModified = () => {
    setValuesModified(true);
  };

  const currentPrice = ordersData?.data.current_price || 0;
  const {
    amountData,
    quantityData,
    isLoading: amountsDataLoading,
    resetAmountChanges,
    convertAmountToQuantity,
    getMinAmountByPrice,
  } = useTradeAmounts({
    symbol,
    accountId,
    precisionConfig,
    skip: !isEdit,
    leverage: ordersData?.data.leverage || 1,
    currentPrice,
    defaultAmount: originalTradeAmount,
    defaultQuantity: originalTradeExistingQuantity,
    reverse: !!ordersData?.data.reverse,
  });

  const {
    entryOrders,
    takeProfitOrders,
    stopOrders,
    setEntryOrders,
    setTakeProfitOrders,
    setStopOrders,
    resetOrdersChanges,
    updateOrder,
    getOrdersByType,
    cancelOrder,
    moveOrderToCurrentPrice,
    moveStopOrderToBreakevenPrice,
    breakevenPrice,
    addOrder,
    updateOrdersWithAddedAmount,
    updateOrdersWithAddedQuantity,
    activeEntryOrders,
    setOrdersByType,
  } = useEditTradeOrders({
    amountData,
    quantityData,
    onValuesModified,
  });

  if (activeEntryOrders) {
    amountData.getAvailable = () => {
      const addedAmount =
        sumBy(activeEntryOrders, (order) => order.edit_amount) -
        sumBy(activeEntryOrders, (order) => order.amount);
      return amountData.available - addedAmount;
    };
  }

  const resetChanges = () => {
    if (tab === TRADE_EXTRA_DETAILS_TABS.ORDERS) {
      resetOrdersChanges();
      resetAmountChanges();
    } else if (tab === TRADE_EXTRA_DETAILS_TABS.CONFIGURATION) {
      cancelConfigurationChanges();
    }
    setValuesModified(false);
  };

  const onCancelChangesClick = async (onCancelChangesCallback?: () => void) => {
    const onCancelChanges = () => {
      resetChanges();
      onCancelChangesCallback?.();
    };

    if (valuesModifiedRef.current) {
      const userWantsToStay = await showUnsavedChangesWarning();
      if (!userWantsToStay) onCancelChanges();
    } else {
      onCancelChanges();
    }
  };

  const convertOrdersToEditRequestFormat = (
    orders: EditOrderData[]
  ): EditTradeTarget[] => {
    return orders.map((order) => {
      const isClosed = [
        OrderStatus.CANCELLED,
        OrderStatus.PARTIALLY_FULFILLED,
      ].includes(order.status);
      return [
        order.id === -1 ? undefined : order.id,
        order.edit_target_price,
        order.edit_ratio,
        order.status !== OrderStatus.PENDING,
        order.number,
        isClosed ? order.amount : order.edit_amount,
        order.filled_amount,
        order.edit_quantity,
        order.reason.toLowerCase() === "existing quantity",
        order.ratio_filled,
      ];
    });
  };

  const exitEditMode = (response?: { data: number }) => {
    exitEditModeProps(response);
    resetChanges();
  };

  const priceLimits = getMinAndMaxPrices(
    currentPrice,
    precisionConfig?.price || 0,
    priceFactorLimits
  );

  const convertEditOrdersToValidation = (
    orders: EditOrderData[]
  ): ValidationOrderData[] =>
    orders.map((order) => ({
      price: order.edit_target_price,
      percent: multiplyFloats(order.edit_ratio, 100),
      amount: order.edit_amount,
      status: order.status,
    }));

  const validationOrdersData: ValidationOrdersData = {
    [OrderTypes.ENTRY]: convertEditOrdersToValidation(entryOrders),
    [OrderTypes.TAKE_PROFIT]: convertEditOrdersToValidation(takeProfitOrders),
    [OrderTypes.STOP]: convertEditOrdersToValidation(stopOrders),
  };
  const errors = validateEditTradeOrders(
    validationOrdersData,
    !!ordersData?.data.reverse,
    !!ordersData?.data.breakout,
    currentPrice,
    priceLimits.min,
    priceLimits.max,
    amountData,
    quantityData
  );

  const saveOrdersChanges = async () => {
    const isValid = validateSaveOrder(
      false,
      errors,
      valuesModified,
      openMessage
    );
    if (isValid && tradeId) {
      if (
        tradeMetadata?.is_automated &&
        tradeMetadata.source === TradeSource.SIGNAL_BOT
      ) {
        const clickedYes = await showConfirmEditWarning();
        if (!clickedYes) return;
      }
      const editTradeRequest = {
        trade_id: tradeId,
        buy_section: convertOrdersToEditRequestFormat(entryOrders),
        sell_section: convertOrdersToEditRequestFormat(takeProfitOrders),
        stop_section: convertOrdersToEditRequestFormat(stopOrders),
      };
      displayResultMessage(
        editTrade(editTradeRequest).unwrap(),
        t("success.trade_edited"),
        t("errors.edit_failed"),
        5,
        exitEditMode
      ).catch(() => {});
    }
  };

  const saveConfigurationChanges = () => {
    if (!valuesModified) {
      openMessage(t("warnings.no_modifications"), "warning");
    } else if (tradeId) {
      displayResultMessage(
        editTradeConfiguration({
          trade_id: tradeId,
          configuration: modifiedConfiguration,
        }).unwrap(),
        t("success.config_edited"),
        t("errors.config_edit_failed"),
        5,
        exitEditMode
      ).catch(() => {});
    }
  };

  const onSaveChangesClick = () => {
    if (tab === TRADE_EXTRA_DETAILS_TABS.ORDERS) {
      saveOrdersChanges().catch(() => {});
    } else if (tab === TRADE_EXTRA_DETAILS_TABS.CONFIGURATION) {
      saveConfigurationChanges();
    }
  };

  const weightedAverageEntryPrice =
    getEditWeightedAverageEntryPrice(entryOrders);

  const functionsRef = useRef({
    onConfirm: onSaveChangesClick,
    onCancel: onCancelChangesClick,
  });
  functionsRef.current = {
    onConfirm: onSaveChangesClick,
    onCancel: onCancelChangesClick,
  };

  const ordersAnnotations = useMemo(() => {
    if (!getTradeExtraInfoOrdersStatus.data || !isEdit) return [];
    return getTradeAnnotations(
      appTheme,
      entryOrders,
      takeProfitOrders,
      stopOrders,
      precision,
      true,
      getTradeExtraInfoOrdersStatus.data?.data.current_price
    );
  }, [
    isEdit,
    getTradeExtraInfoOrdersStatus.data,
    appTheme,
    entryOrders,
    takeProfitOrders,
    stopOrders,
    precision,
  ]);

  const editTradeAnnotations = useMemo(() => {
    return [...activitiesAnnotations, ...ordersAnnotations];
  }, [activitiesAnnotations, ordersAnnotations]);

  return (
    <EditTradeContext.Provider
      value={{
        addAmountModalOpen,
        setAddAmountModalOpen,
        addQuantityModalOpen,
        setAddQuantityModalOpen,
        updateOrder,
        entryOrders,
        takeProfitOrders,
        stopOrders,
        setEntryOrders,
        setTakeProfitOrders,
        setStopOrders,
        amountsDataLoading,
        amountData,
        quantityData,
        precisionConfig,
        getOrdersByType,
        setOrdersByType,
        cancelOrder,
        moveOrderToCurrentPrice,
        moveStopOrderToBreakevenPrice,
        breakevenPrice,
        confirmWithCallback,
        currentPrice,
        ordersData,
        addOrder,
        onSaveChangesClick,
        onCancelChangesClick,
        valuesModified,
        onValuesModified,
        updateOrdersWithAddedAmount,
        updateOrdersWithAddedQuantity,
        editTradeLoading,
        editTradeConfigurationLoading,
        ordersErrors: errors,
        generalConfiguration,
        convertAmountToQuantity,
        getMinAmountByPrice,
        weightedAverageEntryPrice,
        isBotTrade: !!activeEntryOrders,
        activeEntryOrders,
        isEdit,
        functionsRef,
        editTradeAnnotations,
      }}
    >
      {children}
      {confirmModalContextHolder}
    </EditTradeContext.Provider>
  );
};

export default EditTradeWrapper;
